import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import Tooltip from './Tooltip';
import { addDocumentChangeListener, removeDocumentChangeListener, dispatchDocumentChangeEvent } from '../eventSystem';
import EditDocumentModal from './EditDocumentModal';
import { showConfirm, showAlert } from '../utils/ModalService';


interface Document {
  id: string;
  file_name: string;
  document_type: string;
  description?: string;
  created_at: string;
}

interface PropertyDocumentsProps {
  propertyId: string;
}

const formatDate = (dateStr: any) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US');
};

const PropertyDocuments: React.FC<PropertyDocumentsProps> = ({ propertyId }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/documents?property_id=${propertyId}`);
      setDocuments(response.data);
    } catch (err: any) {
      console.error('Error fetching documents:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Error fetching documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchDocuments();
    }

    addDocumentChangeListener(fetchDocuments);

    return () => {
      removeDocumentChangeListener(fetchDocuments);
    };
  }, [propertyId]);

  const handleViewDocument = async (documentId: string) => {
    setMessage('Opening document...');
    try {
      const response = await api.get(`/documents/${documentId}/view`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string }));
      window.open(url, '_blank');
      setMessage('');
    } catch (error: any) {
      console.error('Error viewing document:', error.response?.data || error.message);
      setMessage('Could not open document. Please ensure you are logged in.');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    setMessage('');
    showConfirm('Confirm Action', 'Are you sure you want to delete this document?', async () => {
      try {
        await api.delete(`/documents/${documentId}`);
        setMessage('Document deleted successfully!');
        dispatchDocumentChangeEvent(); // Dispatch event
      } catch (error: any) {
        console.error('Error deleting document:', error.response?.data || error.message);
        setMessage(error.response?.data?.detail || 'An error occurred during document deletion.');
      }
    });
  };

  const handleEditDocument = (doc: Document) => {
    setEditingDocument(doc);
  };

  const handleCloseModal = () => {
    setEditingDocument(null);
  };

  if (loading) {
    return <div>{t("propertyDocuments.k1")}</div>;
  }

  if (error) {
    return <div className="text-danger">{t("propertyDocuments.k2")} {error}</div>;
  }

  return (
    <div className="bg-bg-secondary p-6 rounded-lg shadow border border-border-primary mb-8">
      <EditDocumentModal document={editingDocument} onClose={handleCloseModal} />
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-primary">{t("propertyDocuments.k3")}</h2>
        <Tooltip text="All documents associated with this property are listed here. AI automatically generates descriptions, performs lease abstraction, and extracts general property insights from uploaded documents." />
      </div>
      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.length === 0 ? (
          <p className="text-text-secondary">{t("propertyDocuments.k4")}</p>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="bg-bg-tertiary p-4 rounded shadow border border-border-secondary">
              <h4 className="text-lg font-semibold mb-2 text-text-primary">{doc.file_name}</h4>
              <p className="text-text-secondary"><strong>{t("propertyDocuments.k5")}</strong> {doc.document_type}</p>
              {doc.description && <p className="text-text-secondary mt-2"><strong>{t("propertyDocuments.k6")}</strong> {doc.description}</p>}
              <p className="text-text-secondary text-sm mt-2"><strong>{t("propertyDocuments.k7")}</strong> {formatDate(doc.created_at)}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => handleViewDocument(doc.id)} className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-sm">{t("propertyDocuments.k8")}</button>
                <button onClick={() => handleEditDocument(doc)} className="bg-success text-white px-3 py-1 rounded hover:bg-success-dark text-sm">{t("propertyDocuments.k9")}</button>
                <button onClick={() => handleDeleteDocument(doc.id)} className="bg-danger text-white px-3 py-1 rounded hover:bg-red-700 text-sm">{t("propertyDocuments.k10")}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PropertyDocuments;
